import { FC, KeyboardEvent, PointerEvent, useRef, useState } from 'react'

import { styled } from 'styled-components'

import Clickable from 'core/components/lib/Clickable'
import { Popover, PopoverClose, PopoverContent, PopoverPortal, PopoverTrigger } from 'core/components/lib/Popover'
import { onSurface, outline, surfaceBright } from 'core/styles'

import { DEFAULT_HSV, clampRatio, hexToHsv, hsvToHex, ownHue, readHsv, toLongHex, type Hsv } from './helpers'

type ColorPickerProps = {
  label: string
  value: string
  onChange: (hex: string) => void
}

const ARROW_STEPS: { [key: string]: [number, number] } = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, 1],
  ArrowDown: [0, -1],
}

const percent = (ratio: number) => Math.round(ratio * 100)

const ColorPicker: FC<ColorPickerProps> = ({ label, value, onChange }) => {
  const squareRef = useRef<HTMLDivElement>(null)

  // Black and grey carry no hue, so the square would jump to red the moment a drag reached
  // either. The remembered hue holds it there.
  const [hue, setHue] = useState(() => hexToHsv(value)?.hue ?? DEFAULT_HSV.hue)

  // A colour typed into the field beside this states its own hue, and that has to become
  // the remembered one, or a later grey falls back to a hue the user never picked.
  const stated = ownHue(value)
  if (stated !== undefined && stated !== hue) setHue(stated)

  const hsv: Hsv = readHsv(value, hue)

  const emit = (next: Partial<Hsv>) => {
    if (next.hue !== undefined) setHue(next.hue)
    onChange(hsvToHex({ ...hsv, ...next }))
  }

  const readSquare = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    emit({
      saturation: clampRatio((event.clientX - box.left) / box.width),
      value: 1 - clampRatio((event.clientY - box.top) / box.height),
    })
  }

  const readHue = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    emit({ hue: clampRatio((event.clientX - box.left) / box.width) * 359 })
  }

  const drag = (read: (event: PointerEvent<HTMLDivElement>) => void) => ({
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId)
      read(event)
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) read(event)
    },
  })

  const onSquareKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = ARROW_STEPS[event.key]
    if (!step) return

    event.preventDefault()
    emit({
      saturation: clampRatio(hsv.saturation + step[0] / 100),
      value: clampRatio(hsv.value + step[1] / 100),
    })
  }

  const onHueKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = ARROW_STEPS[event.key]
    if (!step) return

    event.preventDefault()
    emit({ hue: Math.min(359, Math.max(0, hsv.hue + step[0] + step[1])) })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Chip $color={toLongHex(value)} aria-label={`Pick ${label.toLowerCase()}`} type='button' />
      </PopoverTrigger>
      {/* Drawn from the document root, because a field card isolates its own stacking
          context and clips whatever overflows it. */}
      <PopoverPortal>
        <Panel
          align='start'
          // Landing on the close button rings it the moment the panel opens, which reads
          // as an error. The square is what the user came to use.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            squareRef.current?.focus()
          }}
          side='bottom'
          sideOffset={8}
        >
          <Header>
            <Title>{label}</Title>
            <PopoverClose asChild>
              <Clickable aria-label={`Close the ${label.toLowerCase()} picker`} size='sm' variant='ghost'>
                ×
              </Clickable>
            </PopoverClose>
          </Header>
          <Square
            $hue={hsv.hue}
            aria-label={`${label} saturation and brightness`}
            aria-valuemax={100}
            aria-valuemin={0}
            aria-valuenow={percent(hsv.saturation)}
            aria-valuetext={`Saturation ${percent(hsv.saturation)}%, brightness ${percent(hsv.value)}%`}
            onKeyDown={onSquareKeyDown}
            ref={squareRef}
            role='slider'
            tabIndex={0}
            {...drag(readSquare)}
          >
            <SquareThumb $left={percent(hsv.saturation)} $top={percent(1 - hsv.value)} />
          </Square>
          <Hue
            aria-label={`${label} hue`}
            aria-valuemax={359}
            aria-valuemin={0}
            aria-valuenow={Math.round(hsv.hue)}
            onKeyDown={onHueKeyDown}
            role='slider'
            tabIndex={0}
            {...drag(readHue)}
          >
            <HueThumb $hue={hsv.hue} $left={(hsv.hue / 359) * 100} />
          </Hue>
        </Panel>
      </PopoverPortal>
    </Popover>
  )
}

export default ColorPicker

// Block, so the chip does not sit on the text baseline of the slot it is placed in. White
// stands in for an unset colour: beside an empty field it reads as an empty chip.
const Chip = styled.button<{ $color?: string }>`
  display: block;
  border: 1px solid ${outline};
  border-radius: 6px;
  background: ${(p) => p.$color ?? surfaceBright};
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
`

const Panel = styled(PopoverContent)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 12px;
  padding: 12px;
  width: 240px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  color: ${onSurface};
  font-size: 14px;
  font-weight: 500;
`

// White to the hue across, black to the bottom: the standard saturation and value plane.
const Square = styled.div<{ $hue: number }>`
  position: relative;
  border-radius: 8px;
  background:
    linear-gradient(to top, #000, rgb(0 0 0 / 0%)), linear-gradient(to right, #fff, hsl(${(p) => p.$hue}deg 100% 50%));
  touch-action: none;
  height: 148px;
`

const Hue = styled.div`
  position: relative;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    hsl(0deg 100% 50%),
    hsl(60deg 100% 50%),
    hsl(120deg 100% 50%),
    hsl(180deg 100% 50%),
    hsl(240deg 100% 50%),
    hsl(300deg 100% 50%),
    hsl(359deg 100% 50%)
  );
  touch-action: none;
  height: 12px;
`

// Centred on its position, and ringed in both colours so it stays visible at either end.
const Thumb = styled.span`
  display: block;
  position: absolute;
  transform: translate(-50%, -50%);
  outline: 1px solid rgb(0 0 0 / 30%);
  border: 2px solid ${surfaceBright};
  border-radius: 50%;
`

const SquareThumb = styled(Thumb)<{ $left: number; $top: number }>`
  top: ${(p) => p.$top}%;
  left: ${(p) => p.$left}%;
  width: 14px;
  height: 14px;
`

const HueThumb = styled(Thumb)<{ $hue: number; $left: number }>`
  top: 50%;
  left: ${(p) => p.$left}%;
  background: hsl(${(p) => p.$hue}deg 100% 50%);
  width: 16px;
  height: 16px;
`
